import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedText } from "@/i18n/LocalizedText";
import { useState } from "react";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "@/firebase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "sonner";
import { TriangleAlert, Check } from "lucide-react";
import { Link } from "react-router-dom";

const SUCCESS_MESSAGE =
  "If an account exists for that email, a reset link has been sent.";

const ForgotPasswordPage = () => {
  const { t } = useLocale();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email, {
        url: `${window.location.origin}/login`,
      });
      toast.success(SUCCESS_MESSAGE, {
        icon: (
          <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
            <Check className="w-4 h-4 text-black" />
          </div>
        ),
        position: "bottom-right",
      });
      setEmail("");
    } catch (err: any) {
      const code = String(err?.code || "");
      if (code === "auth/too-many-requests") {
        toast.error(
          t("legacy.too.many.attempts.please.try.again.later.x3me5n"),
          {
            icon: (
              <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
                <TriangleAlert className="w-4 h-4 text-black" />
              </div>
            ),
            position: "bottom-right",
          },
        );
      } else {
        toast.success(SUCCESS_MESSAGE, {
          icon: (
            <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
              <Check className="w-4 h-4 text-black" />
            </div>
          ),
          position: "bottom-right",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6">
      <div className="w-full max-w-md border rounded-lg p-6">
        <h1 className="text-2xl font-semibold text-center mb-2">
          <LocalizedText id="legacy.reset.your.password.1eq2me6" />
        </h1>
        <p className="text-center text-sm text-muted-foreground mb-6">
          <LocalizedText id="legacy.enter.your.email.and.well.send.you.a.yycz0r" />
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label
              htmlFor="email"
              className="mb-1 text-sm font-medium text-gray-700"
            >
              <LocalizedText id="legacy.email.3brsq1" />
            </Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </div>

          <Button
            type="submit"
            className="hover:bg-[#ff8138] cursor-pointer bg-[#FF5E00] text-white w-full"
            disabled={loading}
          >
            {loading ? (
              <div className="mr-2 flex justify-center items-center">
                <Spinner variant="circle-filled" size={16} />
              </div>
            ) : null}
            <LocalizedText id="legacy.send.reset.link.h3788c" />
          </Button>
        </form>

        <div className="mt-6 text-center text-sm text-muted-foreground">
          <Link to="/login" className="underline underline-offset-2 text-black">
            <LocalizedText id="legacy.back.to.sign.in.16hh1k1" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
